import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import InvoiceCreateWizard from "../../Finance/InvoiceCreateWizard";
import { apiUrl } from "../../ApiManager/AxiosHeaders";
import { getInvoiceStatusLabel } from "../../../utilis/invoiceStatusHelpers";
import { extractNextInvoiceNumberFromResponse } from "../../../utilis/invoiceNumberHelpers";
import {
  createCommissionInvoice,
  fetchCommissionInvoice,
  fetchCommissionInquiryFees,
  normalizeCommissionInquiryFeeRecord,
  fetchNewCommissionPlan,
  listCommissionInvoices,
  updateCommissionInvoice,
  voidCommissionInvoice,
} from "./commissionsApi";
import { canAccessCommissionInvoiceEditor } from "../../../utilis/invoicePermissionHelpers";
import { buildCommissionDetailInvoicesReturnUrl } from "./commissionDetailUtils";
import { applyCommissionInvoiceLineTotals } from "./commissionInvoiceLineSuggestions";
import { buildProviderDisplayProfileFromApi } from "../../../utilis/applicationCostHelpers";
import { mergeProviderBillToForInvoice } from "./commissionBillToUtils";
import { mapFinanceInvoicePayloadTaxTypesForApi } from "../../../utilis/invoiceCreateHelpers";
import {
  countCommissionInvoicePayments,
  mapCommissionPaymentsFromPayload,
  resolveCommissionPaymentTargetForIndex,
} from "./commissionInvoicePaymentHelpers";
import {
  applyCommissionApproveFields,
  sumCommissionPayloadPaidAmount,
} from "./commissionInvoiceApproveHelpers";
import { mapCommissionInvoiceToWizardRecord } from "./commissionInvoiceRecordMapper";
import { showCommissionInvoiceSuccessToast } from "./commissionInvoiceToast";
import { normalizeInvoiceStatus } from "../../../utilis/invoiceStatusHelpers";
import {
  applyFinanceInvoiceMutationFromResponse,
  applyInvoiceNotesMutationFromResponse,
  normalizeFinanceInvoiceFromRecord,
  normalizeInvoiceNotesList,
  resolveFinanceInvoiceTransactions,
  resolveInvoicePaidAmount,
} from "../../../utilis/invoiceEditHelpers";
import {
  buildFinanceInvoicePaymentFormData,
  buildMultipartFormDataRequestConfig,
  getFinanceInvoicePaymentApiMessage,
  isFinanceInvoicePaymentApiSuccess,
  recordFinanceInvoicePaymentsSequentially,
} from "../../../utilis/financeInvoicePaymentHelpers";
import {
  parseCommissionDetailIdsFromUrl,
  pickClientDisplayName,
  pickClientDob,
  pickClientStudentId,
  pickProviderDisplayName,
  suggestionMetaFromViewModel,
} from "./commissionInvoiceSuggestionMeta";
import {
  buildCommissionInvoicePlanMeta,
  normalizeSavedCommissionPlan,
} from "./detail/commissionPlanUtils";
import { mapPlanPayloadInstallments } from "./detail/commissionInstallmentUtils";
import {
  applyInvoiceCatalog,
  fetchCommissionInvoiceDetail,
  fetchInvoiceCatalog,
  fetchInvoiceNoteList,
  mapWorkspacePlanInvoiceIndex,
  planPayloadFromWorkspace,
  suggestionViewModelFromWorkspaceApplication,
  workspaceErrorMessage,
} from "../../../utilis/invoiceWorkspaceApi";
import "../../Clients/clients.css";
import "./commissions-responsive.css";

function inquiryFeeRecordHasBillableAmounts(record = {}) {
  return (
    (Number(record.application_fee) || 0) > 0.009 ||
    (Number(record.material_fee) || 0) > 0.009 ||
    (Number(record.application_fee_remaining) || 0) > 0.009 ||
    (Number(record.material_fee_remaining) || 0) > 0.009
  );
}

async function loadInquiryFeeRecordForCreateInvoice({
  applicationId,
  clientId,
  providerId,
  applicationCourseId,
}) {
  const primary = await fetchCommissionInquiryFees({
    applicationId,
    clientId,
    providerId,
    applicationCourseId: applicationCourseId || undefined,
  });

  if (inquiryFeeRecordHasBillableAmounts(primary)) {
    return primary;
  }

  if (applicationCourseId) {
    const withoutCourse = await fetchCommissionInquiryFees({
      applicationId,
      clientId,
      providerId,
    });
    if (inquiryFeeRecordHasBillableAmounts(withoutCourse)) {
      return withoutCourse;
    }
  }

  return primary;
}

function readSummaryInquiryFeeAmounts(summary, feeKey) {
  const block = summary?.inquiry_fees?.[feeKey];
  if (!block || typeof block !== "object") {
    return { planned: 0, invoiced: 0 };
  }
  const planned =
    Number(block.planned ?? block.expected ?? block.amount) || 0;
  const invoiced = Number(block.invoiced) || 0;
  return { planned, invoiced };
}

/** When inquiry-fee GET misses the row, use plan summary (same source as detail cards). */
function mergeInquiryFeeRecordFromPlanSummary(record = {}, summary = null) {
  if (inquiryFeeRecordHasBillableAmounts(record) || !summary) {
    return record;
  }

  const app = readSummaryInquiryFeeAmounts(summary, "application_fee");
  const mat = readSummaryInquiryFeeAmounts(summary, "material_fee");
  if (app.planned <= 0.009 && mat.planned <= 0.009) {
    return record;
  }

  const applicationFee = app.planned;
  const materialFee = mat.planned;
  const applicationFeeInvoiced =
    app.invoiced || Number(record.application_fee_invoiced) || 0;
  const materialFeeInvoiced =
    mat.invoiced || Number(record.material_fee_invoiced) || 0;

  return {
    ...record,
    application_fee: applicationFee,
    material_fee: materialFee,
    application_fee_invoiced: applicationFeeInvoiced,
    material_fee_invoiced: materialFeeInvoiced,
    application_fee_remaining: Math.max(0, applicationFee - applicationFeeInvoiced),
    material_fee_remaining: Math.max(0, materialFee - materialFeeInvoiced),
    currency: record.currency || summary?.currency || "AUD",
  };
}

function collectInstallmentIdsFromPayload(payload) {
  const lines = Array.isArray(payload?.line_items) ? payload.line_items : [];
  const commissionIds = [];
  const bonusIds = [];
  lines.forEach((line) => {
    if (line?.commission_installment_id) {
      commissionIds.push(Number(line.commission_installment_id));
    }
    if (line?.bonus_installment_id) {
      bonusIds.push(Number(line.bonus_installment_id));
    }
  });
  return {
    commission_installment_ids: [...new Set(commissionIds.filter(Boolean))],
    bonus_installment_ids: [...new Set(bonusIds.filter(Boolean))],
  };
}

function payloadHasBillableInvoiceLines(payload) {
  const lines = Array.isArray(payload?.line_items) ? payload.line_items : [];
  return lines.some((line) => {
    const feeType = String(line?.fee_type || "").toLowerCase();
    const hasDesc = String(line?.description || "").trim();
    const hasAmount =
      (Number(line?.unit_price) || 0) > 0 && (Number(line?.quantity) || 0) > 0;
    if (
      (feeType === "application_fee" || feeType === "material_fee") &&
      hasAmount
    ) {
      return true;
    }
    const linked =
      line?.commission_installment_id || line?.bonus_installment_id;
    return hasDesc && hasAmount && !linked;
  });
}

function resolveCreatedInvoiceId(createResult) {
  const data = createResult?.raw?.data ?? createResult?.raw;
  return createResult?.invoice?.id || data?.invoice?.id || data?.id || null;
}

function CommissionCreateInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId: clientIdParam } = useParams();
  const clientId = String(clientIdParam || "").trim();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );

  const context = location.state || {};
  const planIdFromQuery = searchParams.get("planId") || context.planId || null;
  const applicationId =
    context.applicationId || searchParams.get("appId") || null;
  const applicationType =
    context.applicationType || searchParams.get("type") || "education";

  const returnTo = useMemo(() => {
    if (location.state?.returnTo) return location.state.returnTo;
    const fromQuery = searchParams.get("returnTo");
    if (fromQuery) return fromQuery;

    // Rebuild a short detail URL from create-invoice query params.
    const detailQuery = new URLSearchParams();
    if (applicationId) detailQuery.set("app_id", String(applicationId));
    if (applicationType) detailQuery.set("type", String(applicationType));
    if (planIdFromQuery) detailQuery.set("planId", String(planIdFromQuery));
    if (clientId) detailQuery.set("student_id", String(clientId));
    const providerId =
      context.providerId || searchParams.get("providerId") || "";
    const courseId = context.courseId || searchParams.get("courseId") || "";
    if (providerId) detailQuery.set("provider_id", String(providerId));
    if (courseId) detailQuery.set("course_id", String(courseId));
    const qs = detailQuery.toString();
    return qs ? `/commissions/detail?${qs}` : "/commissions-invoices";
  }, [
    location.state,
    searchParams,
    applicationId,
    applicationType,
    planIdFromQuery,
    clientId,
  ]);

  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [invoiceSetting, setInvoiceSetting] = useState(null);
  const [discountFeeRules, setDiscountFeeRules] = useState([]);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [planCommissionInvoices, setPlanCommissionInvoices] = useState([]);
  const [nextInvoiceSequence, setNextInvoiceSequence] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [invoiceNotes, setInvoiceNotes] = useState([]);
  const [loadingInvoiceNotes, setLoadingInvoiceNotes] = useState(false);
  const [addingInvoiceNote, setAddingInvoiceNote] = useState(false);
  const [savingFinancePayment, setSavingFinancePayment] = useState(false);
  const [reversingFinancePayment, setReversingFinancePayment] = useState(false);
  const [voidingInvoice, setVoidingInvoice] = useState(false);
  const [planId, setPlanId] = useState(planIdFromQuery);
  const [commissionPlan, setCommissionPlan] = useState(null);
  const [commissionInstallments, setCommissionInstallments] = useState(
    () => context.commissionInstallments || [],
  );
  const [bonusInstallments, setBonusInstallments] = useState(
    () => context.bonusInstallments || [],
  );
  const [meta, setMeta] = useState({
    currency: context.currency || "AUD",
    courseName: context.courseName || "",
    providerName: context.providerName || "",
    studentName: context.studentName || "",
    studentId: context.studentId || "",
    studentDob: context.studentDob || "",
    startDate: context.startDate || "",
    endDate: context.endDate || "",
    commissionRate: context.commissionRate || "",
    commissionType: context.commissionType || "percentage",
    providerId: context.providerId || searchParams.get("providerId") || "",
    courseId: context.courseId || searchParams.get("courseId") || "",
    applicationCourseId:
      context.applicationCourseId ||
      searchParams.get("application_course_id") ||
      searchParams.get("courseId") ||
      "",
  });
  const [inquiryFeeRecord, setInquiryFeeRecord] = useState(() =>
    normalizeCommissionInquiryFeeRecord(context.inquiryFeeRecord || {}),
  );
  const [providerBillTo, setProviderBillTo] = useState(() =>
    context.provider
      ? buildProviderDisplayProfileFromApi(context.provider)
      : buildProviderDisplayProfileFromApi({
          name: context.providerName || "",
        }),
  );
  const [invoiceCatalog, setInvoiceCatalog] = useState(null);

  const wizardApplication = useMemo(
    () =>
      applicationId
        ? { id: Number(applicationId), currency: meta.currency || "AUD" }
        : null,
    [applicationId, meta.currency],
  );

  const installmentSuggestionSource = useMemo(
    () => ({
      commissionInstallments,
      bonusInstallments,
      applicationId,
      applicationType,
      currency: meta.currency,
      courseName: meta.courseName,
      providerName: meta.providerName,
      studentName: meta.studentName,
      studentId: meta.studentId,
      studentDob: meta.studentDob,
      startDate: meta.startDate,
      endDate: meta.endDate,
      defaultCommissionRate: meta.commissionRate,
      commissionType:
        commissionPlan?.commissionType || meta.commissionType || "percentage",
      applicationFee: inquiryFeeRecord?.application_fee ?? 0,
      materialFee: inquiryFeeRecord?.material_fee ?? 0,
      applicationFeeInvoiced: inquiryFeeRecord?.application_fee_invoiced ?? 0,
      materialFeeInvoiced: inquiryFeeRecord?.material_fee_invoiced ?? 0,
      applicationFeeRemaining: inquiryFeeRecord?.application_fee_remaining ?? null,
      materialFeeRemaining: inquiryFeeRecord?.material_fee_remaining ?? null,
      includeInquiryFees: true,
      currentInvoiceId: null,
      commissionInvoices: planCommissionInvoices,
    }),
    [
      commissionInstallments,
      bonusInstallments,
      applicationId,
      applicationType,
      meta,
      commissionPlan,
      inquiryFeeRecord,
      planCommissionInvoices,
    ],
  );

  const commissionPlanMeta = useMemo(
    () => buildCommissionInvoicePlanMeta(commissionPlan),
    [commissionPlan],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadData = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const hasStateInstallments =
          (Array.isArray(context.commissionInstallments) &&
            context.commissionInstallments.length > 0) ||
          (Array.isArray(context.bonusInstallments) &&
            context.bonusInstallments.length > 0);

        const [workspaceResult, nextInvoiceResult, catalogResult] =
          await Promise.allSettled([
            fetchCommissionInvoiceDetail({
              clientId,
              planId: planIdFromQuery || undefined,
              signal: controller.signal,
            }),
            apiUrl.get("getNextInvoiceNumber", { signal: controller.signal }),
            fetchInvoiceCatalog({ signal: controller.signal }),
          ]);

        if (cancelled) return;

        if (workspaceResult.status !== "fulfilled") {
          throw workspaceResult.reason || new Error("Failed to load invoice details.");
        }

        const workspace = workspaceResult.value || {};
        const catalogPayload =
          catalogResult.status === "fulfilled" ? catalogResult.value : null;
        const catalog = applyInvoiceCatalog(catalogPayload || {});
        setInvoiceCatalog(catalogPayload || null);
        setInvoiceSetting(catalog.setting);
        setDiscountFeeRules(catalog.discountRules);

        const clientRecord = workspace.client || null;
        setClientData(clientRecord);

        if (clientRecord) {
          const clientName = pickClientDisplayName(clientRecord);
          const clientDob = pickClientDob(clientRecord);
          const clientStudentId = pickClientStudentId(clientRecord);
          setMeta((prev) => ({
            ...prev,
            studentName: prev.studentName || clientName || "",
            studentId: prev.studentId || clientStudentId || "",
            studentDob: prev.studentDob || clientDob || "",
          }));
        }

        const planRows = mapWorkspacePlanInvoiceIndex(
          workspace.plan_invoices_index,
        );
        setExistingInvoices(
          planRows.map((row) => ({
            id: row.id,
            invoice_number: row.invoiceNumber,
          })),
        );
        setPlanCommissionInvoices(planRows);

        const nextInvoiceResponse =
          nextInvoiceResult.status === "fulfilled"
            ? nextInvoiceResult.value
            : null;
        setNextInvoiceSequence(
          extractNextInvoiceNumberFromResponse(nextInvoiceResponse),
        );

        const planPayload = planPayloadFromWorkspace(workspace);
        const normalizedPlan = planPayload.plan
          ? normalizeSavedCommissionPlan(planPayload.plan)
          : null;
        if (normalizedPlan?.id) {
          setPlanId(normalizedPlan.id);
          setCommissionPlan(normalizedPlan);
        } else if (planIdFromQuery) {
          setPlanId(planIdFromQuery);
        }

        const { commissionInstallments: planInstallments, bonusInstallments: planBonusInstallments } =
          mapPlanPayloadInstallments(planPayload);

        if (planInstallments.length || planBonusInstallments.length) {
          setCommissionInstallments(planInstallments);
          setBonusInstallments(planBonusInstallments);
        } else if (hasStateInstallments) {
          setCommissionInstallments(context.commissionInstallments || []);
          setBonusInstallments(context.bonusInstallments || []);
        }

        const urlIds = parseCommissionDetailIdsFromUrl(
          location.state?.returnTo || location.search || "",
        );
        let detailMeta = suggestionMetaFromViewModel(
          suggestionViewModelFromWorkspaceApplication(workspace.application),
          {
            courseName: context.courseName || "",
            providerName: context.providerName || "",
            studentName: context.studentName || "",
            studentId: context.studentId || "",
            studentDob: context.studentDob || "",
            startDate: context.startDate || "",
            endDate: context.endDate || "",
            commissionRate:
              normalizedPlan?.commissionRate || context.commissionRate || "",
            commissionType:
              normalizedPlan?.commissionType ||
              context.commissionType ||
              "percentage",
            providerId:
              context.providerId ||
              urlIds.providerId ||
              normalizedPlan?.providerId ||
              workspace.application?.provider_id ||
              "",
            courseId:
              context.courseId ||
              urlIds.courseId ||
              normalizedPlan?.courseId ||
              workspace.application?.course_id ||
              "",
            applicationId: applicationId || urlIds.applicationId || null,
            applicationType:
              applicationType || urlIds.applicationType || "education",
          },
        );

        const resolvedAppId =
          applicationId ||
          urlIds.applicationId ||
          normalizedPlan?.applicationId ||
          workspace.application?.application_id ||
          null;

        if (workspace.provider_bill_to?.detail) {
          setProviderBillTo((prev) =>
            mergeProviderBillToForInvoice(
              prev,
              buildProviderDisplayProfileFromApi(
                workspace.provider_bill_to.detail,
                Array.isArray(workspace.provider_bill_to.persondetail)
                  ? workspace.provider_bill_to.persondetail
                  : [],
              ),
            ),
          );
        }

        setMeta((prev) => ({
          ...prev,
          currency: normalizedPlan?.currency || prev.currency || "AUD",
          commissionRate:
            detailMeta.commissionRate ||
            normalizedPlan?.commissionRate ||
            prev.commissionRate ||
            "",
          commissionType:
            normalizedPlan?.commissionType ||
            detailMeta.commissionType ||
            prev.commissionType ||
            "percentage",
          courseName: prev.courseName || detailMeta.courseName || "",
          providerName:
            prev.providerName ||
            detailMeta.providerName ||
            pickProviderDisplayName(planPayload) ||
            "",
          studentName: prev.studentName || detailMeta.studentName || "",
          studentId: prev.studentId || detailMeta.studentId || "",
          studentDob: prev.studentDob || detailMeta.studentDob || "",
          startDate: prev.startDate || detailMeta.startDate || "",
          endDate: prev.endDate || detailMeta.endDate || "",
          providerId:
            prev.providerId ||
            detailMeta.providerId ||
            normalizedPlan?.providerId ||
            "",
          courseId:
            prev.courseId ||
            detailMeta.courseId ||
            normalizedPlan?.courseId ||
            "",
          applicationCourseId:
            prev.applicationCourseId ||
            detailMeta.applicationCourseId ||
            normalizedPlan?.applicationCourseId ||
            workspace.application?.application_course_id ||
            urlIds.applicationCourseId ||
            searchParams.get("application_course_id") ||
            searchParams.get("courseId") ||
            "",
        }));

        const resolvedProviderId =
          detailMeta.providerId ||
          context.providerId ||
          urlIds.providerId ||
          normalizedPlan?.providerId ||
          searchParams.get("providerId") ||
          "";
        const resolvedApplicationCourseId =
          detailMeta.applicationCourseId ||
          normalizedPlan?.applicationCourseId ||
          workspace.application?.application_course_id ||
          context.applicationCourseId ||
          urlIds.applicationCourseId ||
          searchParams.get("application_course_id") ||
          searchParams.get("courseId") ||
          "";
        if (resolvedAppId && clientId && resolvedProviderId) {
          try {
            let feeRecord = await loadInquiryFeeRecordForCreateInvoice({
              applicationId: resolvedAppId,
              clientId,
              providerId: resolvedProviderId,
              applicationCourseId: resolvedApplicationCourseId || "",
            });
            feeRecord = mergeInquiryFeeRecordFromPlanSummary(
              feeRecord,
              planPayload?.summary || null,
            );
            if (!cancelled) {
              setInquiryFeeRecord(feeRecord);
            }
          } catch (feeError) {
            console.error(
              "Failed to load inquiry fee record for invoice suggestions",
              feeError,
            );
          }
        }

        if (!clientRecord) {
          Swal.fire("Error", "Failed to load client details.", "error");
        }
      } catch (error) {
        if (cancelled) return;
        const detail = workspaceErrorMessage(
          error,
          "Failed to load invoice details.",
        );
        if (!detail) return;
        console.error(error);
        Swal.fire("Error", detail, "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Intentionally depend on ids only — location.state is read once for bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, planIdFromQuery, applicationId]);

  const commissionShellStatus = useMemo(() => {
    if (activeInvoice?.approved) return "approved";
    if (activeInvoice?.status) {
      return normalizeInvoiceStatus(activeInvoice.status);
    }
    return "draft";
  }, [activeInvoice?.approved, activeInvoice?.status]);

  const isDraftLikeFlow = !activeInvoice?.id;

  const fetchInvoiceNotes = useCallback(
    async (targetInvoiceId, { silent = false } = {}) => {
      if (!targetInvoiceId) {
        setInvoiceNotes([]);
        return [];
      }
      try {
        if (!silent) setLoadingInvoiceNotes(true);
        const notes = normalizeInvoiceNotesList({
          data: {
            success: true,
            data: await fetchInvoiceNoteList(targetInvoiceId, {
              module: "commission",
            }),
          },
        });
        setInvoiceNotes(notes);
        return notes;
      } catch (error) {
        console.error("Failed to load invoice notes", error);
        return [];
      } finally {
        if (!silent) setLoadingInvoiceNotes(false);
      }
    },
    [],
  );

  const refreshActiveInvoiceRecord = useCallback(
    async (targetInvoiceId) => {
      if (!targetInvoiceId) return null;
      try {
        const invoicePayload = await fetchCommissionInvoice(targetInvoiceId);
        const mapped = mapCommissionInvoiceToWizardRecord(invoicePayload);
        if (mapped) {
          const refreshed = applyCommissionInvoiceLineTotals(
            {
              ...mapped,
              approved: Boolean(mapped?.approved ?? activeInvoice?.approved),
              _commissionInstallmentIds: mapped._commissionInstallmentIds
                ?.length
                ? mapped._commissionInstallmentIds
                : activeInvoice?._commissionInstallmentIds || [],
              _bonusInstallmentIds: mapped._bonusInstallmentIds?.length
                ? mapped._bonusInstallmentIds
                : activeInvoice?._bonusInstallmentIds || [],
              _planId:
                mapped._planId || activeInvoice?._planId || planId || null,
            },
            commissionInstallments,
            bonusInstallments,
            { defaultCommissionRate: meta.commissionRate || "" },
          );
          setActiveInvoice(refreshed);
          return refreshed;
        }
      } catch (error) {
        console.error("Failed to refresh commission invoice", error);
      }
      return null;
    },
    [
      activeInvoice,
      commissionInstallments,
      bonusInstallments,
      meta.commissionRate,
      planId,
    ],
  );

  const handleAddFinanceInvoicePayment = async (
    payment,
    receiptFile = null,
    receiptFiles = null,
    options = {},
  ) => {
    const {
      silent = false,
      skipNavigate = false,
      skipRefresh = false,
      skipSavingState = false,
    } = options;
    const targetInvoiceId = activeInvoice?.id;
    if (!targetInvoiceId) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    const requestedAmount = Math.abs(Number(payment?.amount) || 0);
    if (requestedAmount <= 0) {
      Swal.fire(
        "Invalid amount",
        "Enter a payment amount greater than 0.",
        "warning",
      );
      return false;
    }

    const syncedInvoice = applyCommissionInvoiceLineTotals(
      activeInvoice ||
        normalizeFinanceInvoiceFromRecord({ id: targetInvoiceId }),
      commissionInstallments,
      bonusInstallments,
      { defaultCommissionRate: meta.commissionRate || "" },
    );
    const invoiceTotal = Math.max(Number(syncedInvoice?.total_amount) || 0, 0);
    const transactions = resolveFinanceInvoiceTransactions(syncedInvoice);
    const paidAmount = Math.max(resolveInvoicePaidAmount(syncedInvoice), 0);
    const remainingDue = Math.max(invoiceTotal - paidAmount, 0);
    const payAmount = Math.min(requestedAmount, remainingDue);

    if (payAmount <= 0) {
      Swal.fire(
        "Invalid amount",
        "Nothing left to pay on this invoice.",
        "warning",
      );
      return false;
    }

    try {
      if (!skipSavingState) {
        setSavingFinancePayment(true);
      }
      const lineItems =
        syncedInvoice?.line_items || activeInvoice?.line_items || [];
      const paymentTarget = payment?.line_number
        ? {}
        : resolveCommissionPaymentTargetForIndex(
            lineItems,
            countCommissionInvoicePayments(transactions),
          );

      const formData = buildFinanceInvoicePaymentFormData({
        invoiceId: targetInvoiceId,
        clientId,
        payment: {
          ...paymentTarget,
          ...payment,
          amount: payAmount,
        },
        receiptFile,
        receiptFiles,
      });

      const response = await apiUrl.post(
        `invoicepayment/${targetInvoiceId}`,
        formData,
        buildMultipartFormDataRequestConfig(),
      );

      if (!isFinanceInvoicePaymentApiSuccess(response)) {
        Swal.fire(
          "Error",
          getFinanceInvoicePaymentApiMessage(
            response,
            "Failed to record payment.",
          ),
          "error",
        );
        return false;
      }

      setActiveInvoice((prev) =>
        applyCommissionInvoiceLineTotals(
          {
            ...applyFinanceInvoiceMutationFromResponse(
              prev ||
                normalizeFinanceInvoiceFromRecord({ id: targetInvoiceId }),
              response,
            ),
            approved: Boolean(prev?.approved),
          },
          commissionInstallments,
          bonusInstallments,
          { defaultCommissionRate: meta.commissionRate || "" },
        ),
      );
      setInvoiceNotes((prev) =>
        applyInvoiceNotesMutationFromResponse(prev, response),
      );

      if (skipRefresh) {
        return true;
      }

      await refreshActiveInvoiceRecord(targetInvoiceId);
      await fetchInvoiceNotes(targetInvoiceId, { silent: true });

      if (!silent) {
        showCommissionInvoiceSuccessToast(
          getFinanceInvoicePaymentApiMessage(
            response,
            "Payment recorded successfully.",
          ),
        );
      }

      if (!skipNavigate) {
        navigate(buildCommissionDetailInvoicesReturnUrl(returnTo, { planId }));
      }
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to record payment.",
        "error",
      );
      return false;
    } finally {
      if (!skipSavingState) {
        setSavingFinancePayment(false);
      }
    }
  };

  const handleAddFinanceInvoiceBulkPayment = async (entries) => {
    const targetInvoiceId = activeInvoice?.id;

    try {
      setSavingFinancePayment(true);
      const saved = await recordFinanceInvoicePaymentsSequentially(
        entries,
        (payment, receiptFile, receiptFiles, options = {}) =>
          handleAddFinanceInvoicePayment(payment, receiptFile, receiptFiles, {
            ...options,
            skipNavigate: true,
            skipRefresh: true,
            skipSavingState: true,
          }),
      );

      if (saved && targetInvoiceId) {
        await refreshActiveInvoiceRecord(targetInvoiceId);
        await fetchInvoiceNotes(targetInvoiceId, { silent: true });
        showCommissionInvoiceSuccessToast("Payment recorded successfully.");
        navigate(buildCommissionDetailInvoicesReturnUrl(returnTo, { planId }));
      }

      return saved;
    } finally {
      setSavingFinancePayment(false);
    }
  };

  const handleAddInvoiceNote = async (noteText) => {
    const details = String(noteText || "").trim();
    if (!details) {
      Swal.fire("Warning", "Please enter a note.", "warning");
      return false;
    }
    const targetInvoiceId = activeInvoice?.id;
    const invoiceNumber = activeInvoice?.invoice_number;
    if (!targetInvoiceId || !invoiceNumber) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }
    try {
      setAddingInvoiceNote(true);
      const response = await apiUrl.post("addnoteforinvoice", {
        invoice_id: targetInvoiceId,
        invoice_number: invoiceNumber,
        details,
      });
      if (response?.data?.success || response?.data?.status) {
        await fetchInvoiceNotes(targetInvoiceId);
        return true;
      }
      Swal.fire(
        "Error",
        response?.data?.message || "Failed to add note.",
        "error",
      );
      return false;
    } catch (error) {
      Swal.fire("Error", error?.message || "Failed to add note.", "error");
      return false;
    } finally {
      setAddingInvoiceNote(false);
    }
  };

  const handleClose = () => {
    navigate(buildCommissionDetailInvoicesReturnUrl(returnTo, { planId }));
  };

  const handleSaveComplete = useCallback(() => {
    navigate(buildCommissionDetailInvoicesReturnUrl(returnTo, { planId }));
  }, [navigate, returnTo, planId]);

  const refreshPlanBillingContext = useCallback(async () => {
    if (!clientId) return;

    const [invoicesResult, planResult, nextInvoiceResult] =
      await Promise.allSettled([
        planId ? listCommissionInvoices(planId) : Promise.resolve([]),
        planId
          ? fetchNewCommissionPlan({ planId, clientId, applicationId })
          : Promise.resolve(null),
        apiUrl.get("getNextInvoiceNumber"),
      ]);

    const invoicesList =
      invoicesResult.status === "fulfilled" ? invoicesResult.value : [];
    const planPayload =
      planResult.status === "fulfilled" ? planResult.value : null;
    const nextInvoiceResponse =
      nextInvoiceResult.status === "fulfilled" ? nextInvoiceResult.value : null;

    setExistingInvoices(
      Array.isArray(invoicesList)
        ? invoicesList.map((row) => ({
            id: row.id,
            invoice_number: row.invoiceNumber,
          }))
        : [],
    );
    setPlanCommissionInvoices(Array.isArray(invoicesList) ? invoicesList : []);

    if (planPayload?.installments?.length) {
      setCommissionInstallments(planPayload.installments);
    }
    const bonusRows = Array.isArray(planPayload?.bonuses)
      ? planPayload.bonuses.flatMap((bonus) =>
          Array.isArray(bonus?.installments) ? bonus.installments : [],
        )
      : [];
    if (bonusRows.length) {
      setBonusInstallments(bonusRows);
    }

    const resolvedProviderId =
      meta.providerId || searchParams.get("providerId") || "";
    const resolvedApplicationCourseId =
      meta.applicationCourseId ||
      searchParams.get("application_course_id") ||
      searchParams.get("courseId") ||
      "";
    if (applicationId && clientId && resolvedProviderId) {
      try {
        let feeRecord = await loadInquiryFeeRecordForCreateInvoice({
          applicationId,
          clientId,
          providerId: resolvedProviderId,
          applicationCourseId: resolvedApplicationCourseId || "",
        });
        if (planPayload?.summary) {
          feeRecord = mergeInquiryFeeRecordFromPlanSummary(
            feeRecord,
            planPayload.summary,
          );
        }
        setInquiryFeeRecord(feeRecord);
      } catch (feeError) {
        console.error(
          "Failed to refresh inquiry fee billing context",
          feeError,
        );
      }
    }

    setNextInvoiceSequence(
      extractNextInvoiceNumberFromResponse(nextInvoiceResponse),
    );
  }, [
    clientId,
    planId,
    applicationId,
    meta.providerId,
    meta.applicationCourseId,
    searchParams,
  ]);

  const handleCreate = async (payload, options = {}) => {
    const saveAndAnother = Boolean(options?.saveAndAnother);
    const isApprove = Boolean(options?.approve);
    setSaving(true);
    try {
      if (!planId) {
        throw new Error("Commission plan is required to create an invoice.");
      }

      const selectedIds = collectInstallmentIdsFromPayload(payload);
      const hasInstallmentLink =
        selectedIds.commission_installment_ids.length > 0 ||
        selectedIds.bonus_installment_ids.length > 0;
      const hasBillableLine = payloadHasBillableInvoiceLines(payload);

      if (!hasInstallmentLink && !hasBillableLine) {
        Swal.fire(
          "Add a line item",
          "Select an installment, application fee, material fee, or add another line with description and amount.",
          "warning",
        );
        return { success: false };
      }

      let status = payload?.status || "draft";
      const invoiceDate = payload?.issue_date || payload?.invoice_date || null;
      const payments = mapCommissionPaymentsFromPayload(
        payload,
        payload?.line_items || [],
      );

      let requestBody = {
        ...payload,
        ...selectedIds,
        client_id: clientId ? Number(clientId) : null,
        application_id: applicationId ? Number(applicationId) : null,
        application_type: applicationType || "education",
        invoice_number: payload?.invoice_number || null,
        invoice_date: invoiceDate,
        due_date: payload?.due_date || null,
        remarks: payload?.reference || payload?.notes || null,
        status,
        currency: payload?.currency || meta.currency || "AUD",
        branding_id: payload?.branding_id || null,
        branch_id: payload?.branch_id || null,
        line_items: payload?.line_items || [],
        total_amount: payload?.total_amount || null,
        subtotal: payload?.subtotal || null,
        tax_amount: payload?.tax_amount || null,
      };

      if (payments.length > 0) {
        requestBody.payments = payments;
        if (payments.length === 1) {
          requestBody.payment = payments[0];
        }
      }

      if (isApprove) {
        requestBody = applyCommissionApproveFields(requestBody, {
          totalAmount: payload?.total_amount,
          paidAmount: sumCommissionPayloadPaidAmount(payload, payments),
        });
        status = requestBody.status;
      }

      const createResult = await createCommissionInvoice(
        planId,
        mapFinanceInvoicePayloadTaxTypesForApi(requestBody),
      );
      const createdInvoiceId = resolveCreatedInvoiceId(createResult);
      const statusLabel = getInvoiceStatusLabel(status);

      showCommissionInvoiceSuccessToast(
        isApprove
          ? "Invoice approved."
          : `Commission invoice saved as ${statusLabel}.`,
      );

      if (isApprove && saveAndAnother) {
        await refreshPlanBillingContext();
        return { success: true, saveAndAnother: true, stayOnPage: true };
      }

      if (isApprove && createdInvoiceId) {
        const full = await fetchCommissionInvoice(createdInvoiceId);
        const mapped = mapCommissionInvoiceToWizardRecord(full);
        if (mapped) {
          setActiveInvoice(
            applyCommissionInvoiceLineTotals(
              { ...mapped, _planId: mapped._planId || planId || null },
              commissionInstallments,
              bonusInstallments,
              { defaultCommissionRate: meta.commissionRate || "" },
            ),
          );
          await fetchInvoiceNotes(createdInvoiceId, { silent: true });
        }
        return { success: true, stayOnPage: true, invoiceId: createdInvoiceId };
      }

      if (options?.openApproveEmailModal && createdInvoiceId) {
        const full = await fetchCommissionInvoice(createdInvoiceId);
        const mapped = mapCommissionInvoiceToWizardRecord(full);
        if (mapped) {
          setActiveInvoice(
            applyCommissionInvoiceLineTotals(
              { ...mapped, _planId: mapped._planId || planId || null },
              commissionInstallments,
              bonusInstallments,
              { defaultCommissionRate: meta.commissionRate || "" },
            ),
          );
        }
        return { success: true, stayOnPage: true, invoiceId: createdInvoiceId };
      }

      if (saveAndAnother) {
        await refreshPlanBillingContext();
        return { success: true, saveAndAnother: true, stayOnPage: true };
      }

      return { success: true, stayOnPage: false };
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to create commission invoice.",
        "error",
      );
      return { success: false };
    } finally {
      setSaving(false);
    }
  };

  const handleVoidInvoice = async () => {
    const targetId = activeInvoice?.id;
    if (!targetId) return false;

    const invoiceNumber =
      activeInvoice?.invoice_number ||
      activeInvoice?.invoice_no ||
      targetId;

    const confirmed = await Swal.fire({
      title: "Void invoice?",
      text: `Move invoice ${invoiceNumber} to Void? The record will be kept for reference.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Void",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#64748b",
    });

    if (!confirmed.isConfirmed) return false;

    try {
      setVoidingInvoice(true);
      await voidCommissionInvoice(targetId);
      await Swal.fire({
        icon: "success",
        title: "Voided",
        text: "Invoice moved to Void.",
        timer: 1400,
        showConfirmButton: false,
      });
      navigate(
        buildCommissionDetailInvoicesReturnUrl(returnTo, {
          planId,
          invoiceStatusTab: "void",
        }),
      );
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to void commission invoice.",
        "error",
      );
      return false;
    } finally {
      setVoidingInvoice(false);
    }
  };

  const handleUpdate = async ({ id, payload }, options = {}) => {
    const targetId = id || activeInvoice?.id;
    if (!targetId) return false;

    setSaving(true);
    try {
      const isApprove = Boolean(options?.approve);
      const payments = mapCommissionPaymentsFromPayload(
        payload,
        payload?.line_items || [],
      );
      const fromLines = collectInstallmentIdsFromPayload(payload);
      let requestBody = {
        ...payload,
        invoice_date: payload?.issue_date || payload?.invoice_date || null,
        due_date: payload?.due_date || null,
        remarks: payload?.reference || payload?.remarks || null,
        status: payload?.status || activeInvoice?.status || "awaiting_payment",
        commission_installment_ids:
          fromLines.commission_installment_ids.length > 0
            ? fromLines.commission_installment_ids
            : activeInvoice?._commissionInstallmentIds || [],
        bonus_installment_ids:
          fromLines.bonus_installment_ids.length > 0
            ? fromLines.bonus_installment_ids
            : activeInvoice?._bonusInstallmentIds || [],
        line_items: payload?.line_items || [],
        ...(payments.length ? { payments, payment: payments[0] } : {}),
      };

      if (isApprove) {
        requestBody = applyCommissionApproveFields(requestBody, {
          totalAmount: payload?.total_amount,
          paidAmount: sumCommissionPayloadPaidAmount(payload, payments),
        });
      }

      await updateCommissionInvoice(
        targetId,
        mapFinanceInvoicePayloadTaxTypesForApi(requestBody),
      );
      showCommissionInvoiceSuccessToast(
        isApprove ? "Invoice approved." : "Invoice updated.",
      );
      await refreshActiveInvoiceRecord(targetId);
      if (isApprove) {
        await fetchInvoiceNotes(targetId, { silent: true });
      }
      return { success: true, stayOnPage: true };
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Unable to update invoice.",
        "error",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!canAccessCommissionInvoiceEditor()) {
    return (
      <div className="client-invoices-xero client-create-invoice-page cms-commissions-invoice-page p-4">
        <p className="text-muted mb-0">
          You do not have permission to view commission invoices.
        </p>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="client-invoices-xero client-create-invoice-page cms-commissions-invoice-page">
        <p className="text-muted">Client not found.</p>
      </div>
    );
  }

  return (
    <div className="client-invoices-xero client-create-invoice-page cms-commissions-invoice-page">
      <div className="client-invoices-xero__breadcrumb">
        <Link to="/commissions-invoices">Commissions</Link>
        <span aria-hidden="true">›</span>
        <Link to={returnTo}>Detail</Link>
        <span aria-hidden="true">›</span>
        <span>New invoice</span>
      </div>

      <InvoiceCreateWizard
        show
        pageMode
        pageLoading={loading}
        commissionShellStatus={commissionShellStatus}
        onClose={handleClose}
        onSaveComplete={isDraftLikeFlow ? handleSaveComplete : undefined}
        onCreate={isDraftLikeFlow ? handleCreate : undefined}
        onUpdate={activeInvoice ? handleUpdate : undefined}
        onAfterSaveAndAnother={
          isDraftLikeFlow ? refreshPlanBillingContext : undefined
        }
        onInvoiceEmailSent={() => {
          const targetId = activeInvoice?.id;
          if (targetId) fetchInvoiceNotes(targetId, { silent: true });
        }}
        existingInvoice={activeInvoice || undefined}
        clientData={clientData}
        invoiceSetting={invoiceSetting}
        invoiceCatalog={invoiceCatalog}
        saving={saving}
        discountFeeRules={discountFeeRules}
        existingInvoicesForNumbering={existingInvoices}
        clientApplications={[]}
        installmentSuggestionSource={installmentSuggestionSource}
        suggestionLinesOnly
        billToProfile={providerBillTo}
        commissionProviderId={Number(meta.providerId || "") || null}
        onProviderBillToProfileChange={(patch) => {
          setProviderBillTo((prev) => ({
            ...(prev || {}),
            ...patch,
          }));
        }}
        onBillToClientSnapshotUpdate={(client_snapshot) => {
          setActiveInvoice((prev) =>
            prev ? { ...prev, client_snapshot } : prev,
          );
        }}
        application={wizardApplication}
        applicationType={applicationType || "education"}
        startWithEmptyLines
        defaultInvoiceCurrency={meta.currency || "AUD"}
        commissionPlanMeta={commissionPlanMeta}
        nextInvoiceSequence={nextInvoiceSequence}
        loadingNextInvoiceNumber={loading}
        invoiceDataLoadedFromPage
        onAddInvoiceNote={activeInvoice ? handleAddInvoiceNote : undefined}
        addingInvoiceNote={addingInvoiceNote}
        invoiceNotes={invoiceNotes}
        loadingInvoiceNotes={loadingInvoiceNotes}
        onHistoryVisible={
          activeInvoice?.id
            ? () => fetchInvoiceNotes(activeInvoice.id)
            : undefined
        }
        onSaveFinanceInvoicePayment={
          activeInvoice ? handleAddFinanceInvoicePayment : undefined
        }
        onSaveFinanceInvoiceBulkPayment={
          activeInvoice ? handleAddFinanceInvoiceBulkPayment : undefined
        }
        savingFinanceInvoicePayment={savingFinancePayment}
        onReverseFinanceInvoicePayment={null}
        reversingFinanceInvoicePayment={reversingFinancePayment}
        onVoidInvoice={activeInvoice ? handleVoidInvoice : undefined}
        voidingInvoice={voidingInvoice}
      />
    </div>
  );
}

export default CommissionCreateInvoice;
